import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { FlashcardService, FlashcardCategory } from './flashcard.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';

@Component({
  selector: 'app-flashcard-kategori',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ToastModule,
    ConfirmDialogModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule
  ],
  template: `
    <p-toast></p-toast>
    <p-confirmDialog [style]="{width: '450px'}"></p-confirmDialog>
    <div class="card">
      <div class="flex justify-between mb-4">
        <h5 class="text-xl font-semibold">Kategori Flashcard</h5>
        <p-button label="Tambah Kategori" icon="pi pi-plus" (onClick)="addCategory()"></p-button>
      </div>
      <p-table [value]="categories" [paginator]="true" [rows]="10">
        <ng-template pTemplate="header">
          <tr>
            <th>Nama</th>
            <th>Deskripsi</th>
            <th>Aksi</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-category>
          <tr>
            <td>{{category.name}}</td>
            <td>{{category.description || '-'}}</td>
            <td>
              <p-button icon="pi pi-pencil" styleClass="p-button-rounded p-button-success mr-2" (onClick)="editCategory(category)"></p-button>
              <p-button icon="pi pi-trash" styleClass="p-button-rounded p-button-warning" (onClick)="deleteCategory(category)"></p-button>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <p-dialog [header]="isEditMode ? 'Edit Kategori Flashcard' : 'Buat Kategori Flashcard'" [(visible)]="displayDialog" [style]="{width: '450px'}" [modal]="true">
      <form [formGroup]="categoryForm" (ngSubmit)="onSubmit()">
        <div class="p-fluid">
          <div class="field mb-4">
            <label for="name" class="block text-sm font-medium mb-2">Nama</label>
            <input pInputText id="name" formControlName="name" class="w-full" />
          </div>
          <div class="field mb-4">
            <label for="description" class="block text-sm font-medium mb-2">Deskripsi</label>
            <textarea rows="4" pTextarea id="description" formControlName="description" class="w-full"></textarea>
          </div>
        </div>
      </form>
      <ng-template pTemplate="footer">
        <p-button label="Batal" icon="pi pi-times" styleClass="p-button-text" (onClick)="hideDialog()"></p-button>
        <p-button label="Simpan" icon="pi pi-check" styleClass="p-button-primary" type="submit" (onClick)="onSubmit()" [disabled]="!categoryForm.valid"></p-button>
      </ng-template>
    </p-dialog>
  `,
  providers: [MessageService, ConfirmationService]
})
export class FlashcardKategori implements OnInit {
  categories: FlashcardCategory[] = [];
  displayDialog: boolean = false;
  categoryForm!: FormGroup;
  isEditMode: boolean = false;
  editingCategory: FlashcardCategory | null = null;

  constructor(
    private flashcardService: FlashcardService,
    private router: Router,
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.loadCategories();
    this.categoryForm = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });
  }

  loadCategories(): void {
    this.flashcardService.getCategories().subscribe({
      next: (data) => {
        this.categories = data;
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Gagal memuat kategori' });
        console.error('Error loading categories:', err);
      }
    });
  }

  addCategory(): void {
    this.isEditMode = false;
    this.editingCategory = null;
    this.categoryForm.reset();
    this.displayDialog = true;
  }

  hideDialog(): void {
    this.displayDialog = false;
    this.isEditMode = false;
    this.editingCategory = null;
    this.categoryForm.reset();
  }

  onSubmit(): void {
    if (this.categoryForm.valid) {
      if (this.isEditMode && this.editingCategory) {
        // Update existing category
        const updatedCategory = { ...this.editingCategory, ...this.categoryForm.value };
        this.flashcardService.updateCategory(updatedCategory).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Kategori berhasil diperbarui' });
            this.hideDialog();
            this.loadCategories();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Gagal memperbarui kategori' });
            console.error('Error updating category:', err);
          }
        });
      } else {
        // Create new category
        this.flashcardService.addCategory(this.categoryForm.value).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Kategori berhasil dibuat' });
            this.hideDialog();
            this.loadCategories();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Gagal membuat kategori' });
            console.error('Error creating category:', err);
          }
        });
      }
    }
  }

  editCategory(category: FlashcardCategory): void {
    this.isEditMode = true;
    this.editingCategory = category;
    this.categoryForm.patchValue({
      name: category.name,
      description: category.description
    });
    this.displayDialog = true;
  }

  deleteCategory(category: FlashcardCategory): void {
    this.confirmationService.confirm({
      message: 'Apakah Anda yakin ingin menghapus ' + category.name + '?',
      header: 'Konfirmasi',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.flashcardService.deleteCategory(category.id!).subscribe({
          next: () => {
            this.messageService.add({severity:'success', summary: 'Berhasil', detail: 'Kategori Dihapus', life: 3000});
            this.loadCategories();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Gagal menghapus kategori' });
            console.error('Error deleting category:', err);
          }
        });
      }
    });
  }
}
